// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  Label,
  ResponsiveContainer,
} from "recharts";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { ProgressionTrendlineType } from "src/services/exams/types";

type ProgressionTrendlinesChartType = {
  trendline: ProgressionTrendlineType;
  eye: string;
  // The y-axis always spans at least this range, and grows to fit the data
  yRange: [number, number];
  chartHeight: number;
};

type PointShapeProps = {
  cx?: number;
  cy?: number;
  payload?: { isUnreliable: boolean };
};

const POINT_COLOR = "#000";

// Unreliable exams are red so they stand out from the rest
const renderPoint = ({ cx, cy, payload }: PointShapeProps) => (
  <circle
    data-testid="trendline-point"
    cx={cx}
    cy={cy}
    r={5}
    fill={payload?.isUnreliable ? "#E10C0C" : POINT_COLOR}
    stroke={POINT_COLOR}
    strokeWidth={1.5}
  />
);

const ProgressionTrendlinesChart = ({
  trendline,
  eye,
  yRange,
  chartHeight,
}: ProgressionTrendlinesChartType) => {
  const { t } = useTranslation();

  const { metric, unit, fit, series, line } = trendline;

  // x is days since the epoch; label each point with its own date
  const dateByX = useMemo(
    () => new Map(series.map((point) => [point.x, point.date])),
    [series]
  );

  const ticks = useMemo(() => series.map((point) => point.x), [series]);

  const lineData = useMemo(() => [line.start, line.end], [line]);

  const rate =
    typeof fit.slope_per_period === "number" &&
    typeof fit.slope_per_period_unit === "string"
      ? {
          slope: fit.slope_per_period,
          low: fit.slope_ci_95_low_per_period ?? null,
          high: fit.slope_ci_95_high_per_period ?? null,
          unit: fit.slope_per_period_unit,
        }
      : {
          slope: fit.slope_per_year,
          low: fit.slope_ci_95_low,
          high: fit.slope_ci_95_high,
          unit: `${unit}/year`,
        };

  const hasConfidenceInterval = rate.low !== null && rate.high !== null;

  // already includes the metric's unit, e.g. "dB/month"
  const rateUnit = t(rate.unit);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: "8px",
      }}
    >
      <Typography fontSize="14px" fontWeight={600} textAlign="center">
        {t("{{metric}} Trendline - {{eye}}", {
          metric,
          eye: t(`${eye} Eye`),
        })}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          columnGap: "16px",
          rowGap: "4px",
          px: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minHeight: "52px",
          }}
        >
          <div
            style={{
              width: "24px",
              borderTop: "2px dashed #000",
            }}
          />
          <Box>
            <Typography fontSize="12px">
              {t("Slope: {{slope}} {{unit}}", {
                slope: rate.slope.toFixed(2),
                unit: rateUnit,
              })}
            </Typography>
            {hasConfidenceInterval && (
              <Typography fontSize="12px">
                {t("95% CI: {{low}} to {{high}} {{unit}}", {
                  low: rate.low?.toFixed(2),
                  high: rate.high?.toFixed(2),
                  unit: rateUnit,
                })}
              </Typography>
            )}
            {fit.p_value !== null && (
              <Typography fontSize="12px">
                p = {fit.p_value.toFixed(4)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart margin={{ top: 10, right: 35, left: 5, bottom: 10 }}>
          <XAxis
            dataKey="x"
            type="number"
            domain={["dataMin", "dataMax"]}
            ticks={ticks}
            tickFormatter={(x: number) => dateByX.get(x) ?? ""}
            // skip any date that would overlap its neighbour (recharts measures
            // the angled text); the first and last dates always show
            interval="preserveStartEnd"
            minTickGap={4}
            angle={-45}
            textAnchor="end"
            fontSize={12}
            padding={{ left: 15, right: 15 }}
            // room for the angled dates below the axis
            height={70}
          />
          <YAxis
            dataKey="y"
            type="number"
            fontSize={12}
            tickFormatter={(value: number) => value.toFixed(1)}
            domain={[
              (dataMin: number) => Math.min(dataMin, yRange[0]),
              (dataMax: number) => Math.max(dataMax, yRange[1]),
            ]}
          >
            <Label
              value={`${metric} (${unit})`}
              position="insideLeft"
              angle={-90}
              style={{ textAnchor: "middle" }}
            />
          </YAxis>
          {/* drawn after the line so the points sit on top of it */}
          <Line
            data={lineData}
            dataKey="y"
            stroke="#000"
            strokeDasharray="6 4"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
          <Scatter
            data={series}
            shape={renderPoint}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ProgressionTrendlinesChart;
